import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Delete,
  Edit,
  Add,
  ContactPhone,
  Person,
  DirectionsCar,
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const EmergencyContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Passenger search states
  const [passengerSearch, setPassengerSearch] = useState('');
  const [passengerOptions, setPassengerOptions] = useState([]);
  const [passengerLoading, setPassengerLoading] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getEmergencyContacts({
        page: page + 1,
        limit: pageSize,
        userType: userTypeFilter,
        search: search,
      });

      if (response.success) {
        setContacts(response.data.contacts);
        setTotalRows(response.data.pagination.total);
      } else {
        setError(response.message || 'Failed to load emergency contacts');
      }
    } catch (err) {
      setError('Failed to load emergency contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, userTypeFilter, search]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleSearch = () => {
    setPage(0);
    fetchContacts();
  };

  // Search for passengers
  useEffect(() => {
    const searchPassengers = async () => {
      if (passengerSearch.length < 2) {
        setPassengerOptions([]);
        return;
      }

      setPassengerLoading(true);
      try {
        const response = await apiService.searchPassengers(passengerSearch);
        if (response.success) {
          setPassengerOptions(response.data);
        }
      } catch (err) {
        console.error('Search passengers error:', err);
      } finally {
        setPassengerLoading(false);
      }
    };

    const debounce = setTimeout(searchPassengers, 300);
    return () => clearTimeout(debounce);
  }, [passengerSearch]);

  const handleAdd = () => {
    setEditMode(false);
    setSelectedContact(null);
    setSelectedPassenger(null);
    setOpenDialog(true);
  };

  const handleEdit = (contact) => {
    setEditMode(true);
    setSelectedContact(contact);
    setSelectedPassenger({
      id: contact.user_id,
      name: contact.owner_name,
      phone: contact.owner_phone,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this emergency contact?')) {
      return;
    }

    try {
      const response = await apiService.deleteEmergencyContact(id);
      if (response.success) {
        fetchContacts();
      } else {
        alert(response.message || 'Failed to delete emergency contact');
      }
    } catch (err) {
      alert('Failed to delete emergency contact');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let response;
      if (editMode) {
        response = await apiService.updateEmergencyContact(selectedContact.id, {
          name: values.name,
          phone: values.phone,
          relationship: values.relationship,
          is_active: values.is_active,
        });
      } else {
        if (!selectedPassenger) {
          alert('Please select a passenger');
          setSubmitting(false);
          return;
        }
        response = await apiService.addEmergencyContact({
          userId: selectedPassenger.id,
          userType: 'passenger',
          name: values.name,
          phone: values.phone,
          relationship: values.relationship,
        });
      }

      if (response.success) {
        alert(`Emergency contact ${editMode ? 'updated' : 'added'} successfully`);
        setOpenDialog(false);
        fetchContacts();
      } else {
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      alert('Operation failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    phone: Yup.string().required('Phone is required'),
    relationship: Yup.string().required('Relationship is required'),
  });

  const columns = [
    {
      field: 'owner',
      headerName: 'Passenger/Driver',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {params.row.user_type === 'driver' ? (
            <DirectionsCar color="primary" fontSize="small" />
          ) : (
            <Person color="secondary" fontSize="small" />
          )}
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.owner_name || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.owner_phone || '-'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'user_type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.user_type === 'driver' ? 'Driver' : 'Passenger'}
          color={params.row.user_type === 'driver' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Contact Name',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContactPhone color="action" fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Contact Phone',
      width: 150,
    },
    {
      field: 'relationship',
      headerName: 'Relationship',
      width: 130,
      renderCell: (params) => (
        <Chip label={params.row.relationship || 'Other'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          color={params.row.is_active ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Added At',
      width: 180,
      renderCell: (params) =>
        params.row.created_at
          ? new Date(params.row.created_at).toLocaleString()
          : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Emergency Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage emergency contacts for passengers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Emergency Contact
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                label="User Type"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="passenger">Passengers</MenuItem>
                <MenuItem value="driver">Drivers</MenuItem>
              </Select>
            </FormControl>

            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <DataGrid
            rows={contacts}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 50]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
            rowCount={totalRows}
            paginationMode="server"
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Emergency Contact Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContactPhone color="primary" />
            {editMode ? 'Edit' : 'Add'} Emergency Contact
          </Box>
        </DialogTitle>
        <Formik
          initialValues={{
            name: selectedContact?.name || '',
            phone: selectedContact?.phone || '',
            relationship: selectedContact?.relationship || 'Family',
            is_active: selectedContact?.is_active ?? true,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={3}>
                  {!editMode && (
                    <Grid item xs={12}>
                      <Autocomplete
                        options={passengerOptions}
                        getOptionLabel={(option) =>
                          option.name ? `${option.name} (${option.phone})` : ''
                        }
                        value={selectedPassenger}
                        onChange={(e, newValue) => setSelectedPassenger(newValue)}
                        onInputChange={(e, newValue) => setPassengerSearch(newValue)}
                        loading={passengerLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Passenger *"
                            placeholder="Search by name or phone..."
                            helperText="Type at least 2 characters to search"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {passengerLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                      />
                    </Grid>
                  )}

                  {editMode && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Editing contact for: {selectedContact?.owner_name} ({selectedContact?.owner_phone})
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Contact Name *"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      placeholder="e.g., John Doe"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="phone"
                      label="Contact Phone *"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      placeholder="e.g., +91 9876543210"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Relationship *</InputLabel>
                      <Select
                        name="relationship"
                        value={values.relationship}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Relationship *"
                      >
                        <MenuItem value="Family">Family</MenuItem>
                        <MenuItem value="Friend">Friend</MenuItem>
                        <MenuItem value="Spouse">Spouse</MenuItem>
                        <MenuItem value="Parent">Parent</MenuItem>
                        <MenuItem value="Sibling">Sibling</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {editMode && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="is_active"
                          value={values.is_active}
                          onChange={handleChange}
                          label="Status"
                        >
                          <MenuItem value={true}>Active</MenuItem>
                          <MenuItem value={false}>Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                  }}
                >
                  {editMode ? 'Update' : 'Add'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default EmergencyContactsPage;
